<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';

	import {
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
		Button,
		Modal,
	} from '@Shelly/shelly-design-system/index';

	import CloneSettingsStep from './CloneSettingsStep.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	export let showModal = false;
	const dispatch = createEventDispatcher();

	const steps = [
		{
			title: $format('label_device_settings', { default: 'Device settings' }),
			component: CloneSettingsStep,
			list: [
				{
					title: $format('bulletlist_title_open_device_drawer_tap_settings', {
						default: "Open your device drawer tap 'Settings'",
					}),
				},
				{
					title: $format('bulletlist_title_select_apply_settings_configuration', {
						default:
							"Select 'Apply Settings Configuration' to access the new configuration options",
					}),
				},
				{
					title: $format('bulletlist_title_clone_settings_or_save_device_settings_template', {
						default: 'You can either clone settings or save device settings template',
					}),
				},
			],
			src: './images/lottie/cloneDeviceSettings/CloneSettingsOnboardingPart1.json',
		},
		{
			title: $format('label_application_settings', {
				default: 'Application settings',
			}),
			component: CloneSettingsStep,
			list: [
				{
					title: $format('bulletlist_title_open_application_settings', {
						default: 'Open Application Settings',
					}),
				},
				{
					title: $format('bulletlist_title_select_manage_templates', {
						default: "Select 'Manage Templates'",
					}),
				},
				{
					title: $format(
						'bulletlist_title_preview_edit_delete_saved_device_settings_configurations',
						{
							default:
								'You can preview, edit or delete your saved device settings configurations',
						},
					),
				},
			],
			src: './images/lottie/cloneDeviceSettings/CloneSettingsOnboardingPart2.json',
		},
		{
			title: $format('label_bulk_commands', { default: 'Bulk commands' }),
			component: CloneSettingsStep,
			list: [
				{
					title: $format('bulletlist_title_open_bulk_commands', {
						default: 'Open Bulk Commands',
					}),
				},
				{
					title: $format('bulletlist_title_select_apply_settings_configuration', {
						default: "Select 'Apply Settings Configuration'",
					}),
				},
				{
					title: $format('bulletlist_title_choose_more_than_one_device_to_apply_setting_to', {
						default: 'You can choose more than one device to apply setting to',
					}),
				},
			],
			src: './images/lottie/cloneDeviceSettings/CloneSettingsOnboardingPart3.json',
		},
	];

	let currentStepIdx = 0;

	$: isFirstStep = currentStepIdx === 0;
	$: isLastStep = currentStepIdx === steps.length - 1;

	const goToStep = (idx: number) => {
		if (idx >= 0 && idx < steps.length) {
			currentStepIdx = idx;
		}
	};

	const nextStep = () => {
		if (currentStepIdx < steps.length - 1) {
			currentStepIdx += 1;
		}
	};

	const prevStep = () => {
		if (currentStepIdx > 0) {
			currentStepIdx -= 1;
		}
	};

	const closeWizard = () => {
		showModal = false;
		dispatch('close');
	};

	const handleModalClose = () => {
		showModal = false;
		dispatch('close');
	};
</script>

<Modal
	{showModal}
	on:close={handleModalClose}
	title={$format('clone_settings_onboarding_wizard_title', {
		default: 'Clone Settings Onboarding Wizard',
	})}
>
	<Wizard slot="toolbar">
		{#each steps as step, i (i)}
			<WizardItem
				on:click={() => goToStep(i)}
				active={currentStepIdx === i}
				title={step.title}
				disabled={false}
				showMoreDots={false}
				tooltipTitle={step.title}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer activeStepIndex={currentStepIdx} adaptiveHeight>
			{#each steps as step, i (i)}
				<WizardContent active={currentStepIdx === i}>
					<svelte:component this={step.component} list={step.list} src={step.src} />
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if !isFirstStep}
			<Button class="mr-s" variant="secondary" size="m" on:click={prevStep}>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		{#if !isLastStep}
			<Button variant="primary" size="m" on:click={nextStep}>
				{$format('button_next', { default: 'Next' })}
			</Button>
		{:else}
			<Button variant="primary" size="m" on:click={closeWizard}>
				{capFirstChar($format('button_finish', { default: 'Finish' }))}
			</Button>
		{/if}
	</div>
</Modal>
